"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Cpu, FileText, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: "benefit-1",
  title: "Known Starting Condition",
  body: "You are not inheriting an unknown operating or repair history from a previous owner.",
  iconName: "Clock"
}, {
  id: "benefit-2",
  title: "Current Components",
  body: "New equipment can give buyers access to current controllers, components and product documentation, depending on the manufacturer and model.",
  iconName: "Cpu"
}, {
  id: "benefit-3",
  title: "Product Documentation",
  body: "Review the specifications, operating information and available documentation provided for the exact machine before purchase.",
  iconName: "FileText"
}, {
  id: "benefit-4",
  title: "Warranty Where Applicable",
  body: "Warranty coverage varies between products. Check the specific warranty terms shown for the model you are considering.",
  iconName: "ShieldCheck"
}];
export default function WhyBuyNew() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (name: string) => {
    switch (name) {
      case "Clock":
        return <Clock className="size-5 text-primary" strokeWidth={1.75} />;
      case "Cpu":
        return <Cpu className="size-5 text-primary" strokeWidth={1.75} />;
      case "FileText":
        return <FileText className="size-5 text-primary" strokeWidth={1.75} />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" strokeWidth={1.75} />;
      default:
        return null;
    }
  };
  return <section id="why-buy-new" data-nav="light" className="relative w-full bg-background py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 lg:px-16">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <h2 className="font-serif uppercase font-bold text-3xl sm:text-4xl md:text-5xl text-foreground tracking-tight leading-tight">
              Why Buy a New Mechanical Bull?
            </h2>
            <p className="mt-4 font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
              New equipment can make sense when you want a known starting condition and clearly documented information for the machine you are purchasing. That does not mean every new bull is identical, so buyers should still compare the actual specifications, included equipment and support available for each model.
            </p>
          </div>
        </div>

        {/* Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border my-12">
          {benefits.map((item, i) => <motion.div key={item.id} data-index={i} variants={cardVariants} className="bg-card p-6 sm:p-8 flex flex-col justify-between transition-colors duration-180 hover:bg-muted/30">
              <div>
                {/* Spec Plate Header */}
                <div className="flex items-center justify-between pb-6 border-b border-border mb-6">
                  <div className="p-2 border border-border bg-background">
                    {renderIcon(item.iconName)}
                  </div>
                </div>

                <h3 className="font-serif uppercase font-semibold text-xl sm:text-2xl text-foreground tracking-wide mb-3">
                  {item.title}
                </h3>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                  {item.body}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Action Plate & Warranty Clarity Notice */}
        <div className="border border-border p-6 sm:p-8 md:p-10 bg-muted/40 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h4 className="font-serif uppercase font-semibold text-lg sm:text-xl text-foreground tracking-wide mb-2">
              Product Specifications & Enquiries
            </h4>
            <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
              Product specifications, electrical requirements, shipping information and warranty details vary by model. Review the individual listing or send a product enquiry when you need clarification about a particular machine.
            </p>
          </div>

          <div className="shrink-0 w-full sm:w-auto">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-7 py-6 text-base font-serif uppercase font-semibold tracking-wider transition-colors shadow-none">
              <Link to="/RequestQuotePage" className="inline-flex items-center justify-center gap-3">
                <span>Enquire About a Bull</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}